import { useMemo, useState } from 'react';
import { useFeature } from '@vp/core-ui-components';
import { useQuery } from '@tanstack/react-query';
import { getOrderFulfillmentHistory } from '../apis/bopApi';
import { GET_ORDER_FULFILLMENT_HISTORY } from '../constants/reactQueryKeys';
import { useSiteData } from './useSiteData';
import type {
  LineItemFulfillmentHistory,
  OrderFulfillmentHistory,
} from '@vp/fulfillment-history';
import { useShopperId } from './useShopperId';
import type { OrderSummary } from '../types/OrderHistory.types';
import { useOrderSummary } from '../components/contexts/OrderSummaryContext';

/**
 * Returns an empty OrderFulfillmentHistory response for an order with no fulfillment data.
 * This models the BOP default response when no shipment data is available, indicating the
 * UI should fallback to displaying the status mapped from item state
 *
 * @param order
 */
function anEmptyFulfillmentsResponseForOrder(
  order: OrderSummary
): OrderFulfillmentHistory {
  const lineItemsForUnhydratedResponse = order.lineItems.map(
    (li): LineItemFulfillmentHistory => ({
      lineItemId: li.id,
      fulfillments: [],
    })
  );

  return {
    orderNumber: order.orderNumber,
    lineItems: lineItemsForUnhydratedResponse,
  };
}

/**
 * Retrieves the fulfillment history for an order and all of its line items.
 * Only fires when fulfillment history is enabled for site.

 * @returns The order fulfillment history object.
 */
export const useOrderFulfillmentHistory = (): OrderFulfillmentHistory => {
  const { order } = useOrderSummary();
  const { orderNumber } = order;

  const shopperId = useShopperId();

  const { tenantEnvironment: environment } = useSiteData();

  const fulfillmentHistoryEnabled = useFeature('enableFulfillmentHistory');
  const [called, setCalled] = useState(false);

  const { data: orderFulfillmentHistory } = useQuery({
    queryKey: [GET_ORDER_FULFILLMENT_HISTORY, orderNumber, shopperId],

    queryFn: async () => {
      setCalled(true);
      return getOrderFulfillmentHistory({
        orderNumber,
        shopperId,
        environment,
      });
    },

    enabled: !called && fulfillmentHistoryEnabled,
  });
  //  return a default response with an empty fulfillments array per lineItem. This models the BOP default response when no shipment data is available. Prevents type errors from useQuery returning undefined
  return useMemo(
    () => orderFulfillmentHistory ?? anEmptyFulfillmentsResponseForOrder(order),
    [orderFulfillmentHistory, order]
  );
};
